import type { Settings } from '@core/settings/schema.ts';

export type LearningHoverPauseVideo = {
  pause(): void;
  play(): void;
  isPaused(): boolean;
};

export type LearningHoverPauseDeps = {
  getSettings: () => Pick<Settings, 'learningMode' | 'dictationSubtitle'>;
  getVideo: () => LearningHoverPauseVideo | null | undefined;
  /** Defaults to document — used to detect open learning popups. */
  getDocument?: () => Document;
  /** When false, use pointerdown instead of mouseover/mouseout. */
  canHover?: boolean;
};

type Slot = 'main' | 'second';

function resolveSlot(node: EventTarget | null): Slot | null {
  const el = node instanceof Element ? node : null;
  const container = el?.closest?.('#mainSub, #secondSub');
  if (!container) return null;
  return container.id === 'mainSub' ? 'main' : 'second';
}

function slotContainerId(slot: Slot): 'mainSub' | 'secondSub' {
  return slot === 'main' ? 'mainSub' : 'secondSub';
}

/** Which subtitle track learning clicks / tokens / hover-pause target. */
export function isPracticeLearningSlot(
  settings: Pick<Settings, 'dictationSubtitle'>,
  slot: Slot,
): boolean {
  const prefer: Slot =
    settings.dictationSubtitle === 'secondary' || settings.dictationSubtitle === 'second'
      ? 'second'
      : 'main';
  return slot === prefer;
}

function readCanHover(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(hover: hover)').matches;
}

/**
 * Learning-mode UX: hovering a subtitle slot pauses playback;
 * leaving resumes only if it was playing and no learning popup is open.
 * On coarse pointers, pointerdown on the practice slot pauses instead.
 */
export function installLearningHoverPause(deps: LearningHoverPauseDeps): () => void {
  const doc = () => deps.getDocument?.() ?? document;
  const canHover = deps.canHover ?? readCanHover();
  const wasPausedOnEnter: Record<Slot, boolean> = { main: true, second: true };

  const pauseForSlot = (slot: Slot) => {
    const video = deps.getVideo();
    if (!video) return;
    wasPausedOnEnter[slot] = video.isPaused();
    video.pause();
  };

  const onMouseOver = (event: MouseEvent) => {
    const settings = deps.getSettings();
    if (!settings.learningMode) return;
    const slot = resolveSlot(event.target);
    if (!slot || !isPracticeLearningSlot(settings, slot)) return;

    const container = doc().getElementById(slotContainerId(slot));
    if (!container) return;
    const related = event.relatedTarget;
    if (related instanceof Node && container.contains(related)) return;

    pauseForSlot(slot);
  };

  const onMouseOut = (event: MouseEvent) => {
    const settings = deps.getSettings();
    if (!settings.learningMode) return;
    const slot = resolveSlot(event.target);
    if (!slot || !isPracticeLearningSlot(settings, slot)) return;

    const container = doc().getElementById(slotContainerId(slot));
    if (!container) return;
    const related = event.relatedTarget;
    if (related instanceof Node && container.contains(related)) return;

    if (wasPausedOnEnter[slot]) return;
    if (doc().querySelector('.learning-popup')) return;

    deps.getVideo()?.play();
  };

  const onPointerDown = (event: PointerEvent) => {
    const settings = deps.getSettings();
    if (!settings.learningMode) return;
    const slot = resolveSlot(event.target);
    if (!slot || !isPracticeLearningSlot(settings, slot)) return;
    pauseForSlot(slot);
  };

  const root = doc();
  if (canHover) {
    root.addEventListener('mouseover', onMouseOver, true);
    root.addEventListener('mouseout', onMouseOut, true);
    return () => {
      root.removeEventListener('mouseover', onMouseOver, true);
      root.removeEventListener('mouseout', onMouseOut, true);
    };
  }

  root.addEventListener('pointerdown', onPointerDown, true);
  return () => {
    root.removeEventListener('pointerdown', onPointerDown, true);
  };
}
